import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {Button, Fade, Container, Form} from 'react-bootstrap';
import Configuration from '../config';
import Dialog from 'react-bootstrap-dialog';
import DatePicker from 'react-datepicker';
import {FilePond, registerPlugin} from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import {Formik} from 'formik';
import {Redirect} from 'react-router-dom';
import CommonMetaTag from './common-meta-tag';
import {imageTypes} from '../constants';
import {useTranslation} from 'react-i18next';
import {getOwnedEvents} from '../actions/partner';
import 'filepond/dist/filepond.min.css';
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import {FaArrowLeft, FaCalendarAlt, FaCheck, FaImage, FaTrashAlt} from 'react-icons/fa';

registerPlugin(FilePondPluginImagePreview);

const PartnerEvent = ({
  ownedEvent,
  save,
  removeFile,
  token,
  deleteEvent,
  goto,
  ownedRtvChannels
}) => {
  const {t} = useTranslation();
  if (token === null) {
    return (
      <Redirect to="/login"/>
    );
  }

  if (ownedEvent === null) {
    return null;
  }

  const imageEvent = (ownedEvent.imageName || []).map(f => {
    return {
      source: f,
      options: {
        type: 'local'
      }
    };
  });

  let dialog = null;
  const onSave = values => {
    if (ownedEvent.id && new Date(values.endDatetime) < new Date(ownedEvent.endDatetime)) {
      dialog.show({
        title: t('CONFIRM'),
        body: t('SPECIAL_EVENT_SHORTEN_ACCESS_CONFIRM'),
        actions: [
          Dialog.Action(t('SAVE'), () => save(values), 'btn btn-primary'),
          Dialog.Action(t('CANCEL'), null, 'btn btn-secondary')
        ],
        onHide: currentDialog => currentDialog.hide()
      });
      return;
    }
    save(values);
  };
  const onRemove = id => () => {
    dialog.show({
      title: t('CONFIRM'),
      body: t('SPECIAL_EVENT_DELETE_CONFIRM'),
      actions: [
        Dialog.Action(
          t('DELETE'),
          () => deleteEvent(id),
          'btn partner-event-dialog-danger'
        ),
        Dialog.Action(
          t('NO'),
          null,
          'btn partner-event-dialog-secondary'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={Object.assign({}, ownedEvent)}
      onSubmit={onSave}
    >
      {({
        handleSubmit,
        handleChange,
        setFieldValue,
        values
      }) => {
        return (
          <Fragment>
            <CommonMetaTag/>
            <Fade in appear style={{transition: 'opacity 0.30s linear'}}>
              <main className="partner-event-editor-page">
              <Container>
                <section className="partner-event-editor-hero">
                  <span className="partner-events-eyebrow">{t('SPECIAL_EVENTS_MANAGEMENT')}</span>
                  <h1>{values.id ? values.title : t('NEW_SPECIAL_EVENT')}</h1>
                  <p>{t('SPECIAL_EVENT_FORM_INTRO')}</p>
                </section>
                <section className="partner-event-editor-card">
                    <div className="partner-event-editor-section-heading"><span><FaImage/></span><div><h2>{t('SPECIAL_EVENT_COVER')}</h2><p>{t('SPECIAL_EVENT_COVER_HELP')}</p></div></div>
                    <div className="partner-event-cover-upload">
                      {
                        ownedEvent.id === null ?
                          <FilePond
                            files={values.pendingCover ? [values.pendingCover] : []}
                            allowMultiple={false}
                            maxFiles={1}
                            instantUpload={false}
                            labelIdle={t('SPECIAL_EVENT_COVER_DROP')}
                            onupdatefiles={files => setFieldValue('pendingCover', files.length ? files[0].file : null)}
                          /> :
                          <FilePond
                            files={imageEvent}
                            allowMultiple={false}
                            maxFiles={1}
                            server={
                              {
                                url: `${Configuration.apiEndPoint}/partners/`,
                                process: {
                                  url: `./events/${ownedEvent.id}/images`,
                                  ondata: formData => {
                                    formData.append('image_type', imageTypes.event);
                                    return formData;
                                  }
                                },
                                revert: `./events/${ownedEvent.id}/images`,
                                restore: `./events/${ownedEvent.id}/images`,
                                fetch: `./events/${ownedEvent.id}/images`,
                                load: `./events/${ownedEvent.id}/images?load=`,
                                headers: {Authorization: `Bearer ${token}`},
                                remove: (source, load) => {
                                  removeFile(source, ownedEvent.id);
                                  load();
                                }
                              }
                            }
                            labelIdle={t('SPECIAL_EVENT_COVER_DROP')}
                          />
                      }
                      <small>{t('SPECIAL_EVENT_COVER_FORMAT')}</small>
                    </div>
                    <Form onSubmit={handleSubmit} className="partner-event-editor-form">
                      <div className="partner-event-editor-section-heading"><span><FaCalendarAlt/></span><div><h2>{t('SPECIAL_EVENT_DETAILS')}</h2><p>{t('SPECIAL_EVENT_DETAILS_HELP')}</p></div></div>
                      <Form.Group>
                        <Form.Label>{t('TITLE')}*</Form.Label>
                        <Form.Control required name="title" type="text" placeholder={t('TITLE')} maxLength="255" value={values.title || ''} onChange={handleChange}/>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>{t('DESCRIPTION')}*</Form.Label>
                        <Form.Control required name="description" as="textarea" rows={4} placeholder={t('DESCRIPTION')} maxLength="255" value={values.description || ''} onChange={handleChange}/>
                      </Form.Group>
                      <div className="partner-event-editor-dates">
                        <Form.Group>
                          <Form.Label>{t('SPECIAL_EVENT_ACCESS_START')}*</Form.Label><br/>
                          <DatePicker
                            required
                            locale="it"
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            selected={(values.startDatetime && new Date(values.startDatetime)) || null}
                            onChange={val => setFieldValue('startDatetime', val ? new Date(val).toISOString() : null)}/>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('SPECIAL_EVENT_ACCESS_END')}*</Form.Label><br/>
                          <DatePicker
                            required
                            locale="it"
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            selected={(values.endDatetime && new Date(values.endDatetime)) || null}
                            onChange={val => setFieldValue('endDatetime', val ? new Date(val).toISOString() : null)}/>
                        </Form.Group>
                      </div>
                      <Form.Text className="text-muted mb-3">{t('SPECIAL_EVENT_ACCESS_WINDOW_HELP')}</Form.Text>
                      <div className="partner-event-editor-dates">
                        <Form.Group>
                          <Form.Label>{t('SPECIAL_EVENT_CONTENT_START')}*</Form.Label><br/>
                          <DatePicker
                            required
                            locale="it"
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            selected={(values.contentStartDatetime && new Date(values.contentStartDatetime)) || null}
                            onChange={val => setFieldValue('contentStartDatetime', val ? new Date(val).toISOString() : null)}/>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('SPECIAL_EVENT_CONTENT_END')}*</Form.Label><br/>
                          <DatePicker
                            required
                            locale="it"
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            selected={(values.contentEndDatetime && new Date(values.contentEndDatetime)) || null}
                            onChange={val => setFieldValue('contentEndDatetime', val ? new Date(val).toISOString() : null)}/>
                        </Form.Group>
                      </div>
                      <Form.Text className="text-muted mb-3">{t('SPECIAL_EVENT_CONTENT_WINDOW_HELP')}</Form.Text>
                      <Form.Group>
                      <Form.Label>{t('RTV_EVENT_CHANNEL_ID')}*</Form.Label>
                        <Form.Control required as="select" name="rtvChannelId" value={values.rtvChannelId || ''} onChange={handleChange}>
                          <option value="">{t('RTV_EVENT_CHANNEL_ID')}</option>
                          {ownedRtvChannels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="specialEventPrice">
                        <Form.Label>{t('SPECIAL_EVENT_PRICE')}*</Form.Label>
                        <Form.Control
                          required
                          name="price"
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={values.price === null || values.price === undefined ? '' : (Number(values.price) / 100).toFixed(2)}
                          onChange={event => {
                            const rawPrice = event.target.value;
                            setFieldValue('price', rawPrice === '' ? null : Math.round(Number(rawPrice) * 100));
                          }}
                        />
                        <Form.Text className="text-muted">{t('SPECIAL_EVENT_PRICE_HELP')}</Form.Text>
                      </Form.Group>
                      <div className="partner-event-editor-actions">
                      <Button type="submit" className="partner-events-primary-button"><FaCheck/>{t('SAVE')}</Button>
                      <Button type="button" className="partner-events-secondary-button" onClick={goto('partner-events', getOwnedEvents)}><FaArrowLeft/>{t('CANCEL')}</Button>
                      {
                        ownedEvent.id === null ?
                          null :
                          <Button type="button" className="partner-event-delete-button" onClick={onRemove(ownedEvent.id)}><FaTrashAlt/>{t('DELETE')}</Button>
                      }
                      </div>
                    </Form>
                </section>
              </Container>
              </main>
            </Fade>
            <Dialog
              ref={el => {
                dialog = el;
              }}/>
          </Fragment>
        );
      }}
    </Formik>
  );
};

PartnerEvent.propTypes = {
  ownedEvent: PropTypes.object,
  save: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired,
  token: PropTypes.string,
  goto: PropTypes.func.isRequired,
  ownedRtvChannels: PropTypes.array
};

PartnerEvent.defaultProps = {
  ownedEvent: null,
  token: null,
  ownedRtvChannels: []
};

export default PartnerEvent;
